import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import {
  CreateTaskRequest,
  CreateTaskResult,
  JobType,
  RecordTypeDefinition,
  Task,
  TaskAssignees,
  TaskEvent,
  TaskNote,
  TaskListResult,
  TaskOutcome,
  TaskScope
} from '../model/task';
import { HttpInternalService } from './http.service';

/** What the hub sends with a TaskChanged event. */
export interface TaskChange {
  taskId: string;
  status: string;
  organizationId: string;
}

/**
 * Tasks, from api/tasks (Synced-Backend TasksController), and record type definitions from
 * api/record-types. Paths omit the leading "api/" because HttpInternalService prepends it. Errors
 * come back as HTTP errors whose body is { error, errorMessage, errors } -- see `errorMessage()`.
 *
 * Also carries TaskChanged hub events from NotificationsHubService to whichever card or block is
 * showing, the same shape IncomingEmailService uses for arriving email.
 */
@Injectable({
  providedIn: 'root'
})
export class TasksService {
  private readonly _changed = new Subject<TaskChange>();

  /** Every task change the hub reports, whichever organisation. Subscribers filter. */
  readonly changed$ = this._changed.asObservable();

  /** Asks open cards and blocks to reload, e.g. after a request was raised on the page. */
  private readonly _refresh = new Subject<void>();
  readonly refresh$ = this._refresh.asObservable();

  private _recordTypes$: Observable<RecordTypeDefinition[]>;

  constructor(private _http: HttpInternalService) { }

  /** The readable part of an api/tasks error, with any per-field messages. */
  static errorMessage(error: any, fallback = 'Something went wrong.'): { message: string; details: string[] } {
    const body = error?.error;
    return {
      message: body?.errorMessage || (typeof body === 'string' ? body : null) || error?.message || fallback,
      details: Array.isArray(body?.errors) ? body.errors : []
    };
  }

  notifyChanged(change: TaskChange): void {
    if (change?.taskId) { this._changed.next(change); }
  }

  requestRefresh(): void {
    this._refresh.next();
  }

  list(organisationId: string, scope: TaskScope = 'mine', status: 'open' | 'closed' = 'open', take = 20): Observable<TaskListResult> {
    return this._http.getRequest<TaskListResult>(
      `tasks?organisationId=${organisationId}&scope=${scope}&status=${status}&take=${take}`);
  }

  /** Open tasks and the latest closed ones on one record, which may be known by several ids. */
  forRecord(organisationId: string, recordType: string, recordIds: string[]): Observable<Task[]> {
    const ids = recordIds.filter(id => !!id).map(id => encodeURIComponent(id)).join(',');
    return this._http.getRequest<Task[]>(
      `tasks/for-record?organisationId=${organisationId}&recordType=${encodeURIComponent(recordType)}&recordIds=${ids}`);
  }

  get(organisationId: string, taskId: string): Observable<Task> {
    return this._http.getRequest<Task>(`tasks/${taskId}?organisationId=${organisationId}`);
  }

  create(request: CreateTaskRequest): Observable<CreateTaskResult> {
    return this._http.postRequest<CreateTaskResult>('tasks', JSON.stringify(request));
  }

  /** Approve or reject. A rejection needs a comment. */
  decide(organisationId: string, taskId: string, outcome: TaskOutcome, comment?: string): Observable<Task> {
    return this._http.postRequest<Task>(`tasks/${taskId}/decide`, JSON.stringify({ organisationId, outcome, comment }));
  }

  /** A task with stages: finish its stage ('next'; the last one completes it) or send it back ('back', with a comment). */
  moveStage(organisationId: string, taskId: string, action: 'next' | 'back', comment?: string, toStage?: number | null): Observable<Task> {
    return this._http.postRequest<Task>(`tasks/${taskId}/stage`, JSON.stringify({ organisationId, action, comment, toStage }));
  }

  /** Tick (or untick) an item on the checklist of the stage the task is at. */
  tickChecklist(organisationId: string, taskId: string, stage: number, item: number, done: boolean): Observable<Task> {
    return this._http.postRequest<Task>(`tasks/${taskId}/checklist`, JSON.stringify({ organisationId, stage, item, done }));
  }

  /** The job types a task in this organisation can be made from (its own, and its practice's). */
  jobTypes(organisationId: string): Observable<{ jobTypes: JobType[] }> {
    return this._http.getRequest<{ jobTypes: JobType[] }>('tasks/job-types', { organisationId });
  }

  cancel(organisationId: string, taskId: string, comment?: string): Observable<boolean> {
    return this._http.postRequest<boolean>(`tasks/${taskId}/cancel`, JSON.stringify({ organisationId, comment }));
  }

  // SD-1252: the task panel's Activity and Notes tabs, and Reassign.

  events(organisationId: string, taskId: string): Observable<TaskEvent[]> {
    return this._http.getRequest<TaskEvent[]>(`tasks/${taskId}/events?organisationId=${organisationId}`);
  }

  notes(organisationId: string, taskId: string): Observable<TaskNote[]> {
    return this._http.getRequest<TaskNote[]>(`tasks/${taskId}/notes?organisationId=${organisationId}`);
  }

  addNote(organisationId: string, taskId: string, text: string): Observable<TaskNote> {
    return this._http.postRequest<TaskNote>(`tasks/${taskId}/notes`, JSON.stringify({ organisationId, text }));
  }

  /** Hands an open task to a person, or to the admin role (assignToRole 'admin', nobody named). */
  assign(organisationId: string, taskId: string, assignedToUserId?: string | null, assignToRole?: 'admin' | null): Observable<Task> {
    return this._http.postRequest<Task>(`tasks/${taskId}/assign`,
      JSON.stringify({ organisationId, assignedToUserId: assignedToUserId || null, assignToRole: assignToRole || null }));
  }

  assignees(organisationId: string): Observable<TaskAssignees> {
    return this._http.getRequest<TaskAssignees>(`tasks/assignees?organisationId=${organisationId}`);
  }

  /** Record types and their active fields (Admin > Record types). Fetched once per session. */
  recordTypes(): Observable<RecordTypeDefinition[]> {
    if (!this._recordTypes$) {
      this._recordTypes$ = this._http.getRequest<RecordTypeDefinition[]>('record-types').pipe(
        // A failed load is not remembered: the next caller tries again.
        catchError(error => { this._recordTypes$ = null; return throwError(error); }),
        shareReplay(1));
    }
    return this._recordTypes$;
  }

  /** Forgets the cached definitions, after Admin > Record types changes them. */
  resetRecordTypes(): void {
    this._recordTypes$ = null;
  }
}
